import { useMemo } from 'react';

import { familyAdvantagesPredicate, getFamilyContract } from '@services/mcube';

import { MarketLineType } from '@app-types/contract';
import { ACTION_TYPES, Advantage, ImpactsSimulation } from '@app-types/simulator/simulator';
import { SimulatedCart } from '@app-types/simulator/virtual-cart';
import { UserType } from '@app-types/user';

import { EligibleDiscounts, LoginPrompt } from '@components/connection';
import { LineType } from '@components/connection/lines';

import { formatPhoneNumber } from '@helpers/phone-number';

interface ConnectionIncentiveProps {
    user?: UserType;
    simulation?: ImpactsSimulation;
    cart?: SimulatedCart;
}

function getFaiPromo(simulation: ImpactsSimulation): Advantage | undefined {
    const faiOffer = simulation?.proposed?.filter(
        (prop) =>
            prop.applicableOp?.advantages?.some((a) => familyAdvantagesPredicate(a)) &&
            prop.applicableOp?.marketLine === MarketLineType.FAI,
    );
    return faiOffer ? faiOffer[0]?.applicableOp?.advantages?.find((a) => familyAdvantagesPredicate(a)) : undefined;
}

function getProposedLines(simulation: ImpactsSimulation, cart: SimulatedCart | undefined): LineType[] {
    const proposed: LineType[] = [
        {
            label: 'Votre nouvelle Bbox',
            discount: getFaiPromo(simulation)?.discountIncludingTax,
            marketLine: MarketLineType.FAI,
        },
    ];

    const contract = simulation?.proposed?.find(
        (c) =>
            c.applicableOp.marketLine === 'FNB' && c.applicableOp.advantages?.some((a) => familyAdvantagesPredicate(a)),
    );
    if (contract) {
        const discountAmount = contract.applicableOp.advantages?.find(
            (a) => a.familyPack && (a.action === ACTION_TYPES.CONSERVATION || a.action === ACTION_TYPES.SOUSCRIPTION),
        )?.discountIncludingTax;
        if (discountAmount) {
            proposed.push({
                label: cart && cart.quotes.length > 2 ? 'Vos nouveaux forfaits' : 'Votre nouveau forfait',
                discount: discountAmount,
                marketLine: MarketLineType.FNB,
            });
        }
    }

    return proposed;
}

function getEligibleLines(simulation: ImpactsSimulation): LineType[] {
    return getFamilyContract(simulation).map((contract) => ({
        label: formatPhoneNumber(contract.msisdn || contract.voipNumber) || contract.applicableOp?.commercialLabel,
        discount: contract.applicableOp.advantages?.filter(familyAdvantagesPredicate)[0]?.discountIncludingTax,
        marketLine: contract.applicableOp.marketLine as MarketLineType,
    }));
}

export function ConnectionIncentive({ user, simulation, cart }: Readonly<ConnectionIncentiveProps>) {
    const [proposedLines, eligibleLines] = useMemo(() => {
        if (!user || !simulation) {
            return [[], []];
        }

        const proposed = getProposedLines(simulation, cart);
        const eligible = getEligibleLines(simulation);

        return [proposed, eligible];
    }, [simulation, user?.sub]);

    if (!user) {
        return <LoginPrompt />;
    }

    return (
        <EligibleDiscounts
            user={user}
            isLoading={simulation === undefined}
            proposedLines={proposedLines}
            proposedLinesLabel={
                cart && cart.quotes?.length > 2
                    ? 'Vous choisissez une Bbox et des forfaits'
                    : 'Vous choisissez une Bbox et un forfait'
            }
            eligibleLines={eligibleLines}
        />
    );
}
